import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Paper,
  Box,
  Chip,
  CircularProgress,
  List,
  ListItem,
  Divider,
  Avatar,
} from "@mui/material";
import {
  PersonAdd as PersonAddIcon,
  Edit as EditIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  History as HistoryIcon,
} from "@mui/icons-material";
import axiosInstance from "utilities/axios";
import useDialog from "hooks/useDialog";

type AuditLog = {
  _id: string;
  action: "CREATE" | "UPDATE" | "ACTIVATE" | "DEACTIVATE";
  performedBy: {
    _id: string;
    name: string;
    email: string;
  };
  changes: Record<string, { old: any; new: any }>;
  createdAt: string;
};

type UserAuditLogDialogProps = {
  open: boolean;
  onClose: () => void;
  userId: string;
  userName: string;
};

const actionConfig = {
  CREATE: {
    icon: PersonAddIcon,
    color: "success" as const,
    label: "შექმნა",
  },
  UPDATE: {
    icon: EditIcon,
    color: "primary" as const,
    label: "განახლება",
  },
  ACTIVATE: {
    icon: CheckCircleIcon,
    color: "success" as const,
    label: "აქტივაცია",
  },
  DEACTIVATE: {
    icon: CancelIcon,
    color: "error" as const,
    label: "დეაქტივაცია",
  },
};

const fieldLabels: Record<string, string> = {
  name: "სახელი",
  email: "ელ-ფოსტა",
  roles: "როლები",
  permissions: "უფლებები",
  bank: "ბანკი",
  agencyLevel: "სააგენტოს დონე",
  agencyDepartment: "დეპარტამენტი",
  deactivated: "სტატუსი",
};

const formatValue = (value: any): string => {
  if (value === null || value === undefined) return "არ არის";
  if (typeof value === "boolean") return value ? "დიახ" : "არა";
  if (Array.isArray(value)) return value.join(", ");
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

export default function UserAuditLogDialog({
  open,
  onClose,
  userId,
  userName,
}: UserAuditLogDialogProps) {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(false);
  const { showSnackbar } = useDialog();

  useEffect(() => {
    if (open && userId) {
      fetchLogs();
    }
  }, [open, userId]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(`/auth/user/${userId}/audit-logs`);
      setLogs(response.data);
    } catch (error) {
      console.error("Error fetching audit logs:", error);
      showSnackbar("აუდიტის ლოგების ჩატვირთვისას დაფიქსირდა შეცდომა", "error");
    } finally {
      setLoading(false);
    }
  };

  const renderChanges = (changes: Record<string, { old: any; new: any }>) => {
    return Object.entries(changes).map(([field, change]) => (
      <Box key={field} sx={{ mb: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
          {fieldLabels[field] || field}:
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: 2 }}>
          <Chip
            label={formatValue(change.old)}
            size="small"
            variant="outlined"
            color="default"
            sx={{ textDecoration: "line-through" }}
          />
          <Typography variant="body2">→</Typography>
          <Chip label={formatValue(change.new)} size="small" color="primary" />
        </Box>
      </Box>
    ));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <EditIcon />
          <Typography variant="h6">ცვლილებების ისტორია - {userName}</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : logs.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography color="text.secondary">ცვლილებების ისტორია არ არსებობს</Typography>
          </Box>
        ) : (
          <List sx={{ width: "100%" }}>
            {logs.map((log, index) => {
              const config = actionConfig[log.action];
              const Icon = config.icon;
              const date = new Date(log.createdAt);
              const dateStr = date.toLocaleDateString("ka-GE");
              const timeStr = date.toLocaleTimeString("ka-GE", { hour: "2-digit", minute: "2-digit" });

              return (
                <React.Fragment key={log._id}>
                  <ListItem alignItems="flex-start" sx={{ flexDirection: "column", gap: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%" }}>
                      <Avatar sx={{ bgcolor: `${config.color}.main` }}>
                        <Icon />
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                          <Chip label={config.label} color={config.color} size="small" />
                          <Typography variant="body2" color="text.secondary">
                            {log.performedBy.name}
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {dateStr} • {timeStr}
                        </Typography>
                      </Box>
                    </Box>
                    {Object.keys(log.changes).length > 0 && (
                      <Paper variant="outlined" sx={{ p: 2, width: "100%", ml: 7 }}>
                        {renderChanges(log.changes)}
                      </Paper>
                    )}
                  </ListItem>
                  {index < logs.length - 1 && <Divider variant="inset" component="li" />}
                </React.Fragment>
              );
            })}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>დახურვა</Button>
      </DialogActions>
    </Dialog>
  );
}
